import {
  invoiceFailureStatuses,
  invoiceReasonLabels,
  invoiceStatusLabels,
  invoiceWarningStatuses,
  statusColors,
  statusLabels,
  taxAdministrationFilingLabels,
  taxAdministrationResponseLabels,
} from '@/constants'
import { Customer, Order } from '@/types'

export function formatCurrency(amount: number | string) {
  return Number(amount).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
}

export function formatVatRate(rate: string | number) {
  return `${Number(rate).toLocaleString('fr-FR')} %`
}

// Mirrors Rails' String#parameterize: transliterates accents, lowercases, and replaces
// any run of non-alphanumeric characters with a single hyphen.
export function parameterize(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
}

export function formatName(customer: Customer | null | undefined): string {
  if (!customer) return ''
  return `${customer.first_name} ${customer.last_name}`.trim()
}

export function customerLabel(customer: Customer): string {
  return `${formatName(customer)} (${customer.phone_number})`
}

export function getCustomerInfo(order: Pick<Order, 'customer' | 'contract'>): {
  name: string
  phone: string
  email: string | null
  address: string | null
} {
  if (order.customer) {
    return {
      name: formatName(order.customer),
      phone: order.customer.phone_number,
      email: order.customer.email ?? null,
      address: order.customer.address ?? null,
    }
  }
  if (order.contract) {
    return {
      name: order.contract.customer_name,
      phone: order.contract.customer_phone_number,
      email: order.contract.customer_email || null,
      address: order.contract.customer_address || null,
    }
  }
  return { name: '', phone: '', email: null, address: null }
}

export function latestVerifiedAt(order: Pick<Order, 'status' | 'articles'>): string | null {
  if (order.status !== 'ready' && order.status !== 'completed') return null

  const verifiedDates = order.articles
    .flatMap((article) => article.services)
    .map((service) => service.verified_at)
    .filter((date): date is string => Boolean(date))
    .sort()

  return verifiedDates.length ? verifiedDates[verifiedDates.length - 1] : null
}

export function latestVerifier(order: Pick<Order, 'status' | 'articles'>): string | null {
  if (order.status !== 'ready' && order.status !== 'completed') return null

  const verifiedServices = order.articles
    .flatMap((article) => article.services)
    .filter((service) => service.verified_at)
    .sort((a, b) => (a.verified_at! < b.verified_at! ? -1 : 1))

  return verifiedServices.length ? (verifiedServices[verifiedServices.length - 1].verified_by_name ?? null) : null
}

export function formatStatus(order: Pick<Order, 'status' | 'received_at'>, isBoutique: boolean = false) {
  if (isBoutique && ['ready', 'todo', 'to_be_reviewed'].includes(order.status)) {
    if (order.received_at) return 'À retirer'
    return 'Travail en cours'
  }

  return statusLabels[order.status as keyof typeof statusLabels] || order.status
}

export function getStatusColor(order: Pick<Order, 'status' | 'received_at'>, isBoutique: boolean = false) {
  if (isBoutique && ['ready', 'todo', 'to_be_reviewed'].includes(order.status)) {
    if (order.received_at) return 'green'
    return 'blue'
  }
  return statusColors[order.status as keyof typeof statusColors] || 'gray'
}

export function formatInvoiceReason(reason: string) {
  return reason
    .split(' · ')
    .map((part) => invoiceReasonLabels[part] ?? part)
    .join(' · ')
}

export function formatInvoiceStatus(statusCode: string) {
  if (!statusCode.startsWith('ppf:')) return invoiceStatusLabels[statusCode] ?? statusCode

  const filing = statusCode.slice('ppf:'.length)
  const responseKey = Object.keys(taxAdministrationResponseLabels)
    .sort((left, right) => right.length - left.length)
    .find((key) => filing.endsWith(`-${key}`))
  const filingKey = responseKey ? filing.slice(0, -(responseKey.length + 1)) : filing
  const label = `Administration fiscale : ${taxAdministrationFilingLabels[filingKey] ?? filingKey}`
  return responseKey ? `${label} (${taxAdministrationResponseLabels[responseKey]})` : label
}

export type InvoiceStatusTone = 'done' | 'warning' | 'failure'

export function invoiceStatusTone(statusCode: string, statusText?: string | null): InvoiceStatusTone {
  if (invoiceFailureStatuses.includes(statusCode) || /-(ack-error|rejected)$/.test(statusCode)) return 'failure'
  if (invoiceWarningStatuses.includes(statusCode) || statusText?.startsWith('Encaissement annulé')) return 'warning'
  return 'done'
}
